import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import { useDropdownPopover } from '@/shared-components/dropdown';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import Text from '@/shared-components/text/components/Text';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import { hasBusinessBilling } from '../utils/billingMode';

export default function SupportSubscriptionCard({
  subscription,
  isMenuOpen,
  onToggleMenu,
  onRequestCancel,
  onRequestRestart,
  onSwitchBusinessBilling,
}) {
  const {
    supportedUser,
    id,
    supportedUserName,
    supportedUserSubtitle,
    amountLabel,
    supportAmountLabel,
    statusLabel,
    nextBillingLabel,
    isRecurring,
    isCanceling,
  } = subscription;

  const statusNode = isCanceling ? <strong>Cancelled</strong> : statusLabel;
  const supportMetaLabel = isRecurring ? nextBillingLabel : 'One-time contribution';
  const avatarImage = resolveUserProfileImage(
    subscription?.supportedUserProfileImage,
    supportedUser?.user_image,
    supportedUser,
  );
  const typeOfProfile = resolveUserProfileTypeWithFallback(
    'collector',
    subscription?.supportedUserProfileType,
    supportedUser?.user_type,
    supportedUser?.__t,
    supportedUser,
  );
  const avatarProfileType = avatarImage ? typeOfProfile : 'collector';
  const canRestart = Boolean(isCanceling && typeof onRequestRestart === 'function');
  const canCancel = Boolean(!isCanceling && typeof onRequestCancel === 'function');
  const isBusinessBilling = hasBusinessBilling(subscription);
  const canTriggerAction = canRestart || canCancel;
  const actionHandler = canRestart
    ? () => onRequestRestart(subscription)
    : canCancel
      ? () => onRequestCancel(subscription)
      : undefined;
  const actionLabel = canRestart ? 'Activate subscription' : canCancel ? 'Cancel subscription' : 'No actions available';
  const menuId = id ? String(id) : null;
  const handleOpenChange = (nextOpen) => {
    if (nextOpen !== isMenuOpen) onToggleMenu?.(menuId);
  };
  const { rootRef, triggerRef, menuId: dropdownMenuId, toggle, handleTriggerKeyDown } = useDropdownPopover({
    open: isMenuOpen,
    onOpenChange: handleOpenChange,
    disabled: !menuId,
    idPrefix: `support-subscription-${menuId ?? 'actions'}`,
  });
  const handleMenuAction = () => {
    onToggleMenu?.(menuId);
    actionHandler?.();
  };
  const handleSwitchBusinessBilling = () => {
    onToggleMenu?.(menuId);
    onSwitchBusinessBilling?.(subscription);
  };

  return (
    <div className="profile-support-subscriptions__card">
      <div className="profile-support-subscriptions__summary">
        <div className="profile-support-subscriptions__avatar">
          <UserProfileImage image={avatarImage} typeOfProfile={avatarProfileType} />
        </div>
        <div className="profile-support-subscriptions__text">
          <Text className="profile-support-subscriptions__title">
            {isRecurring ? 'Recurring support' : 'One-time support'}
            {supportAmountLabel ? ` (${supportAmountLabel})` : ''}
            {' for '}
            <strong>{supportedUserName}</strong>
          </Text>
          {supportedUserSubtitle && (
            <Text className="profile-support-subscriptions__subtitle">{supportedUserSubtitle}</Text>
          )}
          {(statusLabel || supportMetaLabel) && (
            <Text className="profile-support-subscriptions__meta">
              {statusNode}
              {statusLabel && supportMetaLabel ? ' - ' : ''}
              {supportMetaLabel}
            </Text>
          )}
        </div>
        <div className="dropdown-menu dropdown-menu--wide profile-support-subscriptions__actions" ref={rootRef}>
          <button type="button" className="dropdown-menu__trigger" onClick={toggle} onKeyDown={handleTriggerKeyDown} disabled={!menuId} aria-label="Support subscription options" aria-haspopup="menu" aria-expanded={isMenuOpen} aria-controls={dropdownMenuId} ref={triggerRef}>
            <ThreeDotsHorizontal className="dropdown-menu__icon" />
          </button>
          <div id={dropdownMenuId} className={`dropdown-menu__menu ${isMenuOpen ? 'is-open' : ''}`} role="menu">
            <button type="button" className="dropdown-menu__item" onClick={handleMenuAction} disabled={!canTriggerAction}>
              {actionLabel}
            </button>
            {!isBusinessBilling && (
              <button type="button" className="dropdown-menu__item" onClick={handleSwitchBusinessBilling} disabled={!subscription?.isActive}>
                Buy as business
              </button>
            )}
          </div>
          <Text className="profile-support-subscriptions__amount-value">{amountLabel ?? 'Amount unavailable'}</Text>
        </div>
      </div>
    </div>
  );
}
